import { useState } from 'react'
import {
  addFoodEntry,
  deleteFoodEntry,
  deleteSavedFood,
  logSavedFood,
  setNutritionSettings,
  sortedSavedFoods,
  updateFoodEntry,
} from '../../budget/repo'
import { useStore } from '../../store'
import { Card, TopBar } from '../../ui/parts'
import {
  caloriesOf,
  entriesOn,
  formatGrams,
  formatKcal,
  summarise,
  targetsFor,
  validateSplit,
} from '../nutrition'
import type { FoodEntry, NutritionSettings } from '../types'
import { AddFoodSheet, type FoodDraft } from './AddFoodSheet'
import { MacroBar } from './MacroBar'

export function NutritionScreen() {
  const { snapshot, mutate, showToast } = useStore()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<FoodEntry | null>(null)
  const [showTargets, setShowTargets] = useState(false)

  if (!snapshot) return null

  const summary = summarise(snapshot.nutrition)
  const today = entriesOn(snapshot.nutrition.entries, summary.date)
  const saved = sortedSavedFoods(snapshot)

  async function save(draft: FoodDraft) {
    if (editing) {
      const id = editing.id
      setEditing(null)
      await mutate((s) => updateFoodEntry(s, id, draft))
    } else {
      setAdding(false)
      if (await mutate((s) => addFoodEntry(s, draft))) {
        showToast(draft.alsoSave ? `Added and saved "${draft.label.trim()}"` : `Added "${draft.label.trim()}"`)
      }
    }
  }

  return (
    <div className="screen">
      <TopBar
        title="Nutrition"
        subtitle="Today"
        actions={
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowTargets(!showTargets)}
            aria-label="Targets"
          >
            ⚙
          </button>
        }
      />

      <div className="stack">
        <Card>
          <div className="lbl">Calories remaining</div>
          <div className={`big ${summary.caloriesRemaining < 0 ? 'over' : ''}`}>
            {Math.round(summary.caloriesRemaining)}
          </div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
            {formatKcal(summary.totals.calories)} of {formatKcal(summary.targets.calories)}
          </div>
          {summary.macros.map((macro) => (
            <MacroBar key={macro.key} macro={macro} />
          ))}
        </Card>

        {showTargets ? (
          <TargetsCard
            settings={snapshot.nutrition.settings}
            onSave={async (next) => {
              if (await mutate((s) => setNutritionSettings(s, next))) {
                setShowTargets(false)
                showToast('Targets updated')
              }
            }}
          />
        ) : null}

        {saved.length > 0 ? (
          <Card>
            <div className="card-title" style={{ marginBottom: 4 }}>
              Quick add
            </div>
            {saved.map((food) => (
              <button
                key={food.id}
                type="button"
                className="saved-food"
                onClick={async () => {
                  if (await mutate((s) => logSavedFood(s, food.id))) {
                    showToast(`Added "${food.label}"`)
                  }
                }}
              >
                <span className="grow">
                  <span className="food-name">{food.label}</span>
                  <span className="chips">
                    <span className="macro-chip">P {formatGrams(food.proteinG)}</span>
                    <span className="macro-chip">C {formatGrams(food.carbsG)}</span>
                    <span className="macro-chip">F {formatGrams(food.fatG)}</span>
                  </span>
                </span>
                <span className="kcal">{formatKcal(caloriesOf(food))}</span>
                <span
                  role="button"
                  tabIndex={0}
                  className="icon-btn"
                  aria-label={`Forget ${food.label}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    void mutate((s) => deleteSavedFood(s, food.id))
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation()
                      void mutate((s) => deleteSavedFood(s, food.id))
                    }
                  }}
                >
                  ✕
                </span>
              </button>
            ))}
          </Card>
        ) : null}

        <Card>
          <div className="card-title" style={{ marginBottom: 4 }}>
            Today's log
          </div>
          {today.length === 0 ? (
            <div className="muted" style={{ fontSize: 14, padding: '10px 0' }}>
              No food logged today.
            </div>
          ) : (
            today.map((entry) => (
              <div
                key={entry.id}
                className="food"
                role="button"
                tabIndex={0}
                onClick={() => setEditing(entry)}
                onKeyDown={(e) => e.key === 'Enter' && setEditing(entry)}
              >
                <div className="grow">
                  <div className="food-name">{entry.label}</div>
                  <div className="chips">
                    <span className="macro-chip">P {formatGrams(entry.proteinG)}</span>
                    <span className="macro-chip">C {formatGrams(entry.carbsG)}</span>
                    <span className="macro-chip">F {formatGrams(entry.fatG)}</span>
                  </div>
                </div>
                <span className="kcal">{formatKcal(caloriesOf(entry))}</span>
              </div>
            ))
          )}
        </Card>
      </div>

      <button type="button" className="fab" onClick={() => setAdding(true)} aria-label="Add food">
        +
      </button>

      {adding || editing ? (
        <AddFoodSheet
          editing={editing}
          onSave={save}
          onDelete={
            editing
              ? async () => {
                  const id = editing.id
                  setEditing(null)
                  await mutate((s) => deleteFoodEntry(s, id))
                }
              : undefined
          }
          onClose={() => {
            setAdding(false)
            setEditing(null)
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * The calorie total and the split are editable; the gram targets are shown but never
 * typed, so they cannot drift out of step with the percentages.
 */
function TargetsCard({
  settings,
  onSave,
}: {
  settings: NutritionSettings
  onSave: (settings: NutritionSettings) => void
}) {
  const [calories, setCalories] = useState(String(settings.calorieTarget))
  const [protein, setProtein] = useState(String(settings.proteinPct))
  const [carbs, setCarbs] = useState(String(settings.carbsPct))
  const [fat, setFat] = useState(String(settings.fatPct))

  const next: NutritionSettings = {
    calorieTarget: Number(calories) || 0,
    proteinPct: Number(protein) || 0,
    carbsPct: Number(carbs) || 0,
    fatPct: Number(fat) || 0,
  }
  const problem = validateSplit(next)
  const preview = targetsFor(next)

  return (
    <Card>
      <div className="card-title">Daily targets</div>
      <label className="field" style={{ marginTop: 10 }}>
        <span>Calories</span>
        <input
          type="text"
          inputMode="numeric"
          value={calories}
          onChange={(e) => setCalories(e.target.value.replace(/\D/g, '').slice(0, 5))}
        />
      </label>

      <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
        <PercentField label="Protein %" value={protein} onChange={setProtein} />
        <PercentField label="Carbs %" value={carbs} onChange={setCarbs} />
        <PercentField label="Fat %" value={fat} onChange={setFat} />
      </div>

      {problem ? (
        <div className="over" style={{ fontSize: 13, marginTop: 8 }}>
          {problem}
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
          {formatGrams(preview.proteinG)} protein · {formatGrams(preview.carbsG)} carbs ·{' '}
          {formatGrams(preview.fatG)} fat
        </div>
      )}

      <button
        type="button"
        className="btn small"
        style={{ marginTop: 12 }}
        disabled={problem !== null}
        onClick={() => onSave(next)}
      >
        Save targets
      </button>
    </Card>
  )
}

function PercentField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <label className="field grow" style={{ marginBottom: 0 }}>
      <span>{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 3))}
      />
    </label>
  )
}
